import React, { useState, useEffect, useRef, useCallback } from 'react'
import { ParticleWaveBackground } from './UI/ParticleWaveBackground'

const advantages = [
  {
    title: '无限画布',
    description: '自由自在的创作空间，不受边界限制，随心所欲放置想法。',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-3-3v6" />
      </svg>
    )
  },
  {
    title: '极速同步',
    description: '实时保存您的每一个灵感，多端同步，随时随地开启创作。',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.001 0 01-15.357-2m15.357 2H15" />
      </svg>
    )
  },
  {
    title: '安全分享',
    description: '多种分享模式，支持密码保护、下载权限控制，隐私无忧。',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    )
  },
  {
    title: '多样化内容',
    description: '支持文本、图片、文件等多种格式，打造属于您的知识库。',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    )
  }
]

export const SystemIntroView: React.FC = () => {
  // 动画样式
  const [animStyle, setAnimStyle] = useState({
    x: 0,
    y: 0,
    rotate: 0,
    duration: 0.8
  })
  const [isJumping, setIsJumping] = useState(false)
  // 记录当前方向：1 表示向右，-1 表示向左
  const [_direction, setDirection] = useState(1)
  // 红色区域的引用
  const headerRef = useRef<HTMLDivElement>(null)
  // 自动跳跃的定时器引用
  const jumpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 跳跃到指定位置
  const jumpTo = useCallback((targetX: number) => {
    if (isJumping) return // 正在跳跃中，忽略
    
    // 边界检测
    const maxX = 280
    targetX = Math.max(-maxX, Math.min(maxX, targetX))
    
    // 随机旋转角度
    const rotateAmount = (Math.random() - 0.5) * 2160
    // 跳跃时间
    const duration = 1.0 + Math.random() * 0.5

    setIsJumping(true)
    setAnimStyle(prev => ({
      x: targetX,
      y: -220,
      rotate: prev.rotate + rotateAmount,
      duration: duration / 2
    }))

    // 到达顶点后落下
    setTimeout(() => {
      setAnimStyle(prev => ({
        ...prev,
        y: 0,
        duration: duration / 2
      }))
      
      // 落地后
      setTimeout(() => {
        setIsJumping(false)
      }, duration / 2 * 1000)
    }, duration / 2 * 1000)
  }, [isJumping])

  // 安排下一次自动跳跃
  const scheduleNextAutoJump = useCallback(() => {
    // 随机 2-4 秒后下一次自动跳跃
    const nextDelay = (2 + Math.random() * 2) * 1000
    jumpTimeoutRef.current = setTimeout(() => {
      // 触发自动跳跃事件
      window.dispatchEvent(new CustomEvent('autoJump'))
    }, nextDelay)
  }, [])

  // 处理红色区域点击
  const handleHeaderClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!headerRef.current || isJumping) return
    
    // 清除自动跳跃定时器
    if (jumpTimeoutRef.current) {
      clearTimeout(jumpTimeoutRef.current)
      jumpTimeoutRef.current = null
    }
    
    // 计算点击位置相对于红色区域中心的偏移
    const rect = headerRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const clickX = e.clientX
    const offsetX = clickX - centerX
    
    jumpTo(offsetX)
    
    // 落地后安排下一次自动跳跃
    const duration = 1.0 + Math.random() * 0.5
    setTimeout(() => {
      scheduleNextAutoJump()
    }, duration * 1000 + 100)
  }, [isJumping, jumpTo, scheduleNextAutoJump])

  // 自动跳跃
  useEffect(() => {
    let currentDirection = 1 // 初始向右
    let currentX = 0 // 跟踪当前 x 位置

    const doAutoJump = () => {
      if (isJumping) {
        // 如果正在跳跃，稍后再试
        jumpTimeoutRef.current = setTimeout(doAutoJump, 500)
        return
      }
      
      // 在当前方向上随机跳跃一段距离
      const jumpDistance = 150 + Math.random() * 150
      let targetX = currentX + jumpDistance * currentDirection
      
      // 边界检测，如果超出范围则反弹
      const maxX = 280
      if (targetX > maxX) {
        targetX = maxX - (targetX - maxX) * 0.5
        currentDirection = -1
      } else if (targetX < -maxX) {
        targetX = -maxX - (targetX + maxX) * 0.5
        currentDirection = 1
      }
      
      currentX = targetX
      setDirection(currentDirection)
      jumpTo(targetX)
      
      // 随机 2-4 秒后下一次自动跳跃
      const nextDelay = (2 + Math.random() * 2) * 1000
      jumpTimeoutRef.current = setTimeout(doAutoJump, nextDelay)
    }

    // 监听自动跳跃事件（用户点击后触发）
    const handleAutoJumpEvent = () => {
      doAutoJump()
    }
    window.addEventListener('autoJump', handleAutoJumpEvent)

    // 初始延迟后开始自动跳跃
    jumpTimeoutRef.current = setTimeout(doAutoJump, 2000)

    return () => {
      if (jumpTimeoutRef.current) {
        clearTimeout(jumpTimeoutRef.current)
      }
      window.removeEventListener('autoJump', handleAutoJumpEvent)
    }
  }, [])

  return (
    <div className="relative h-full overflow-y-auto custom-scrollbar" style={{ background: 'transparent' }}>
      {/* Three.js 粒子波浪背景 */}
      <ParticleWaveBackground />
      
      <div className="relative z-10 p-6 md:p-10 max-w-5xl mx-auto flex flex-col">
      {/* 外层相对定位容器，用于放置跳跃的 icon */}
      <div className="relative animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Icon 在最外层，不受任何 overflow-hidden 影响 */}
        <div 
          className="absolute left-1/2 z-30"
          style={{
            top: '208px', // 红色区域底部位置 (256px - 48px padding)
            transform: `translate(calc(-50% + ${animStyle.x}px), ${animStyle.y}px) rotate(${animStyle.rotate}deg)`,
            transition: `transform ${animStyle.duration}s ${isJumping && animStyle.y < 0 ? 'cubic-bezier(0.33, 1, 0.68, 1)' : 'cubic-bezier(0.32, 0, 0.67, 0)'}`
          }}
        >
          <div className="p-4 rounded-[28px] bg-white/20 backdrop-blur-sm shadow-[0_8px_16px_rgba(0,0,0,0.1)] flex items-center justify-center">
            <div className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center bg-transparent">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-full h-full object-contain drop-shadow-2xl"
                style={{ 
                  clipPath: 'circle(50% at center)',
                  WebkitClipPath: 'circle(50% at center)'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://raw.githubusercontent.com/lucide-icons/lucide/main/icons/palette.svg';
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col">
          {/* Header - 红色区域，点击可让 icon 跳到该位置 */}
          <div 
            ref={headerRef}
            onClick={handleHeaderClick}
            className="relative h-64 bg-gradient-to-br from-[#ff5c5c] to-[#ff8a8a] flex flex-col items-center text-white p-6 overflow-hidden cursor-pointer"
          >
            {/* Decorative background circles */}
            <div className="absolute top-[-20%] left-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-[-30%] right-[-5%] w-80 h-80 bg-black/5 rounded-full blur-3xl" />
            
            {/* 标题在顶部 */}
            <div className="z-10 text-center pt-10">
              <h2 className="text-4xl font-black font-comic tracking-tight">无限笔记 (Infinite Note)</h2>
              <p className="text-white/90 mt-2 font-medium text-lg">开启您的无限创意之旅</p>
              <p className="text-white/80 mt-3 text-base">开发者：胡思乱想晕乎乎 | 1868253@qq.com</p>
            </div>
          </div>

        {/* Content */}
        <div className="p-10 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advantages.map((adv, index) => (
              <div 
                key={index} 
                className="bg-[#fff9fa] p-6 rounded-[32px] border border-pink-50 hover:border-pink-200 hover:shadow-xl hover:shadow-pink-500/5 transition-all duration-300 group"
              >
                <div className="flex items-center gap-5 mb-4">
                  <div className="p-4 rounded-2xl bg-white text-[#ff5c5c] shadow-sm group-hover:bg-[#ff5c5c] group-hover:text-white transition-all duration-500">
                    {adv.icon}
                  </div>
                  <h3 className="font-bold text-gray-800 text-xl">{adv.title}</h3>
                </div>
                <p className="text-gray-500 leading-relaxed font-medium">
                  {adv.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-pink-50 to-purple-50 rounded-[40px] border border-pink-100 text-center relative overflow-hidden">
            <div className="relative z-10">
              <h4 className="text-2xl font-black text-gray-800 mb-3 font-comic">为什么选择无限笔记？</h4>
              <p className="text-gray-500 italic text-lg">"在有限生命里，遇见那个有趣的自己"</p>
              <div className="mt-8">
                <div className="inline-flex items-center gap-2 px-6 py-2 bg-white/50 backdrop-blur-sm rounded-full text-sm font-bold text-[#ff5c5c] border border-pink-100">
                  <span className="w-2 h-2 rounded-full bg-[#ff5c5c] animate-ping" />
                  当前版本 v1.0.0
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Footer */}
          <div className="p-6 bg-gray-50/50 text-center text-xs text-gray-400 border-t border-gray-100 font-medium space-y-1">
            <div>&copy; 2026 Infinite Note. Designed with ❤️ for creators.</div>
            <div>
              <a
                href="https://beian.miit.gov.cn"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                蜀ICP备2020028680号-1
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .font-comic {
          font-family: 'Comic Sans MS', 'Microsoft YaHei UI', cursive;
        }
      `}</style>
      </div>
    </div>
  )
}

export default SystemIntroView
